'use client';
import React, { useState, useEffect } from 'react';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import TestimonialCard from './TestimonialCard';
import Image from 'next/image';
import { cn } from '@/lib/utils';

const Testimonials = ({ testimonials, heading }) => {
  const [api, setApi] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setActiveIndex(api.selectedScrollSnap());
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };

    api.on('select', onSelect);
    api.on('reInit', onSelect);
    onSelect();

    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  if (!testimonials || testimonials.length === 0) return null;

  return (
    <div className="w-full px-4">
      <div className="w-full max-w-screen-xl mx-auto py-16 flex flex-col">
        <div className="flex justify-center mb-10 order-1">
          <h2
            className="font-instrument-serif italic sm:not-italic text-3xl sm:leading-[80px] sm:text-[50px] sm:font-inter-tight font-normal text-center"
            style={{
              background:
                'linear-gradient(91.86deg, #1FBCCB 0.28%, rgba(255, 255, 255, 0.88) 100%), #FFFFFF',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0px 0px 29px rgba(31, 188, 203, 0.4)',
            }}
          >
            {Array.isArray(heading) ? (
              <span className="flex flex-wrap justify-center gap-x-[0.3em] font-light">
                {heading.map((item, index) => (
                  <span
                    key={index}
                    className={cn(
                      item.style === 'italic'
                        ? 'font-instrument-serif italic'
                        : 'font-inter-tight not-italic'
                    )}
                  >
                    {item.text}
                  </span>
                ))}
              </span>
            ) : (
              heading
            )}
          </h2>
        </div>

        <Carousel
          setApi={setApi}
          className="w-full order-3 lg:order-2"
          opts={{
            loop: false,
          }}
        >
          <CarouselContent className="-ml-5">
            {testimonials.map((item, index) => (
              <CarouselItem key={index} className="pl-5 basis-full flex justify-center">
                <div className="flex justify-center h-full w-full">
                  <TestimonialCard
                    data={item.testimonials_id}
                    isActive={index === activeIndex}
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        {/* Navigation Arrows */}
        <div className="flex justify-center gap-3 text-white order-2 lg:order-3 mb-4 lg:mb-0 lg:mt-8">
          <button
            onClick={() => api?.scrollPrev()}
            disabled={!canScrollPrev}
            className={cn(
              'w-12 h-12 rounded-full flex items-center justify-center border border-white transition-all duration-300',
              !canScrollPrev
                ? 'opacity-30 cursor-not-allowed'
                : 'hover:shadow-button-glow hover:text-black cursor-pointer bg-transparent hover:bg-white/10'
            )}
          >
            <Image
              src="/icons/arrow-left.svg"
              width={16}
              height={16}
              alt="Prev"
              className="w-4 h-auto"
            />
          </button>
          <button
            onClick={() => api?.scrollNext()}
            disabled={!canScrollNext}
            className={cn(
              'w-12 h-12 rounded-full flex items-center justify-center border border-white transition-all duration-300',
              !canScrollNext
                ? 'opacity-30 cursor-not-allowed'
                : 'hover:shadow-button-glow hover:text-black cursor-pointer bg-transparent hover:bg-white/10'
            )}
          >
            <Image
              src="/icons/arrow-right.svg"
              width={16}
              height={16}
              alt="Next"
              className="w-4 h-4 invert-0"
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Testimonials;
